import { useCallback, useEffect, useState } from 'react';
import { api } from './api';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';

const cache = new Map();
export const getFamilyAlertPolicy = (orgId) => api.get(`/organizations/${orgId}/family-alert-policy`).then((r) => r.data);
const TTL_MS = 60000;
const cachedPolicy = (orgId) => {
  const hit = cache.get(orgId);
  if (hit && Date.now() - hit.at < TTL_MS) return hit.p;
  const p = getFamilyAlertPolicy(orgId).catch((e) => { cache.delete(orgId); throw e; });
  cache.set(orgId, { p, at: Date.now() });
  return p;
};
export const setFamilyAlertPolicy = (orgId, value) =>
  api.patch(`/organizations/${orgId}/family-alert-policy`, { family_alert_actions: value }).then((r) => { cache.delete(orgId); return r.data; });

// Only alerts linked to one resident can be handled by caregivers and family; resident-less ones are admin-only.
export const familyCanActOn = (a) => !!(a?.resident_uuid || a?.resident_id);
export const isAdminUser = (user) => user?.role === 'admin' || user?.is_super_admin === true;

// Current org's family-alert policy (readable by any member); null until loaded or on error.
export function useOrgFamilyPolicy(enabled = true) {
  const { current } = useOrg() || {};
  const [policy, setPolicy] = useState(null);
  useEffect(() => {
    if (!enabled || !current?.id) return undefined;
    let alive = true;
    cachedPolicy(current.id).then((p) => alive && setPolicy(p)).catch(() => alive && setPolicy(null));
    return () => { alive = false; };
  }, [enabled, current?.id]);
  return policy;
}

// Can the current user acknowledge / resolve / note alerts? Staff always; family only when the org allows it.
export function useCanManageAlerts(alert) {
  const { user } = useAuth();
  const isFamily = user?.role === 'family';
  const policy = useOrgFamilyPolicy(isFamily);
  if (alert !== undefined && !isAdminUser(user) && !familyCanActOn(alert)) return false;
  return isFamily ? !!policy?.effective : true;
}

// Settings switch state for admins.
export function useFamilyAlertPolicy(orgId) {
  const [policy, setPolicy] = useState(null);
  const [state, setState] = useState('loading'); // loading | ready | pending | error
  const load = useCallback(() => {
    if (!orgId) return;
    setState('loading');
    getFamilyAlertPolicy(orgId)
      .then((p) => { setPolicy(p); setState('ready'); })
      .catch((e) => setState([404, 405, 501].includes(e?.response?.status) ? 'pending' : 'error'));
  }, [orgId]);
  useEffect(() => { load(); }, [load]);
  return { policy, setPolicy, state, reload: load };
}
